import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"
import { FileText, ArrowRight } from "lucide-react"

export default function BlogIndex() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const comparisons = [
    {
      slug: "fusegu-vs-sucuri",
      title: "Fusegu vs Sucuri: Which Security Platform Delivers Better Value?",
      description: "Detailed comparison of Fusegu and Sucuri security platforms. Discover which solution offers the best value for agencies and small businesses.",
      date: "November 2025",
      readTime: "6 min read",
      category: "Comparison"
    },
    {
      slug: "fusegu-vs-wordfence",
      title: "Fusegu vs Wordfence for Agencies: Which Security Solution is Right for You?",
      description: "Compare Fusegu and Wordfence security solutions for WordPress agencies. Learn about features, pricing, and which tool fits your workflow.",
      date: "November 2025",
      readTime: "5 min read",
      category: "Comparison"
    },
  ]

  const alternatives = [
    {
      slug: "beagle-security-alternative",
      title: "Beagle Security Alternative - 7x Cheaper for Volume Scanning",
      description: "Looking for a Beagle Security alternative? Fusegu offers comprehensive scanning at a fraction of the cost - perfect for agencies managing multiple sites.",
      date: "November 2025",
      readTime: "8 min read",
      category: "Alternative"
    },
    {
      slug: "wordfence-alternative",
      title: "Wordfence Alternative - Multi-Platform Security for Agencies",
      description: "Switch from Wordfence to Fusegu for multi-platform security scanning beyond WordPress. Scan any web application with professional reports.",
      date: "November 2025",
      readTime: "7 min read",
      category: "Alternative"
    },
    {
      slug: "sucuri-alternative",
      title: "Sucuri Alternative - Unlimited Sites for Growing Agencies",
      description: "Looking for a Sucuri alternative? Fusegu offers comprehensive security scanning for unlimited sites at one flat rate.",
      date: "November 2025",
      readTime: "7 min read",
      category: "Alternative"
    },
    {
      slug: "sitelock-alternative",
      title: "SiteLock Alternative - Unlimited Sites for Agencies",
      description: "Switch from SiteLock to Fusegu for multi-site agencies. Scan unlimited sites with white-label reports at one flat rate.",
      date: "November 2025",
      readTime: "8 min read",
      category: "Alternative"
    },
    {
      slug: "ithemes-security-alternative",
      title: "iThemes Security Alternative - Scan Beyond WordPress",
      description: "Looking for an iThemes Security alternative? Fusegu scans WordPress AND any other platform - perfect for diverse agency portfolios.",
      date: "November 2025",
      readTime: "7 min read",
      category: "Alternative"
    },
  ]

  return (
    <>
      <Helmet>
        <title>Security Tips & Guides | Fusegu Blog</title>
        <meta 
          name="description" 
          content="Learn about web security, vulnerability scanning, and best practices. Expert guides, tool comparisons, and security insights from Fusegu." 
        />
        <meta property="og:title" content="Security Resources & Guides | Fusegu Blog" />
        <meta property="og:description" content="Expert security guides, comparisons, and best practices for protecting your applications." />
        <meta property="og:url" content="https://fusegusecurity.com/blogs" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
        <div className="flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <FuseguLogo className="h-6 w-6 flex-shrink-0" />
            <span className="text-lg font-bold">Fusegu</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Security Resources
              </span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Guides, comparisons, and insights to help you make informed security decisions for your applications.
            </p>
          </div>

          {/* Comparisons Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Platform Comparisons</h2>
            <div className="space-y-6">
              {comparisons.map((post) => (
                <Link to={`/blogs/${post.slug}`} key={post.slug}>
                  <Card className="shadow-md hover:shadow-lg transition-all hover:border-blue-200 cursor-pointer">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <CardTitle className="text-xl md:text-2xl mb-2 text-blue-600 hover:text-blue-700">
                            {post.title}
                          </CardTitle>
                          <CardDescription className="text-base">
                            {post.description}
                          </CardDescription>
                        </div>
                        <FileText className="h-6 w-6 text-blue-600 flex-shrink-0" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <span>{post.date}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                        </div>
                        <div className="flex items-center gap-1 text-blue-600 font-medium">
                          Read more
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Alternatives Section */}
          <div>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Looking for Alternatives?</h2>
            <div className="space-y-6">
              {alternatives.map((post) => (
                <Link to={`/${post.slug}`} key={post.slug}>
                  <Card className="shadow-md hover:shadow-lg transition-all hover:border-purple-200 cursor-pointer">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <CardTitle className="text-xl md:text-2xl mb-2 text-purple-600 hover:text-purple-700">
                            {post.title}
                          </CardTitle>
                          <CardDescription className="text-base">
                            {post.description}
                          </CardDescription>
                        </div>
                        <FileText className="h-6 w-6 text-purple-600 flex-shrink-0" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <span>{post.date}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                        </div>
                        <div className="flex items-center gap-1 text-purple-600 font-medium">
                          Read more
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Secure Your Applications?
            </h2>
            <p className="text-lg mb-6 opacity-90">
              Start your free trial today. No credit card required.
            </p>
            <Link to="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8">
                Start Free Security Scan
              </Button>
            </Link>
            <p className="text-sm mt-4 opacity-75">
              7-day free trial • 3 scans per day on any sites • Full feature access
            </p>
          </div>

          {/* Footer Navigation */}
          <div className="mt-12 pt-8 border-t text-center space-y-4">
            <div className="flex justify-center gap-6 text-sm">
              <Link to="/" className="text-blue-600 hover:underline">
                Home
              </Link>
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy
              </Link>
              <Link to="/terms" className="text-blue-600 hover:underline">
                Terms
              </Link>
            </div>
            <p className="text-xs text-gray-500">
              © 2025 Fusegu Security. All rights reserved.
            </p>
          </div>
        </div>
      </main>
      </div>
    </>
  )
}

